// 基于ICOPE知识库的智能内容生成器 - V4架构
import { knowledgeBaseService } from './knowledgeBase';

/**
 * 面向老年人及家属的内容转化规则
 */
const AUDIENCE_TRANSFORMATION_RULES = {
  terminology: {
    '社区卫生工作者': '社区专业人员',
    '社区健康工作者': '社区专业人员', 
    '护理者': '家人或照护者',
    '初级保健机构': '医疗机构',
    '内在能力丧失': '身体功能变化',
    '内在能力损伤': '身体功能下降',
    '深入评估': '详细检查',
    '基础评估': '初步评估',
    '干预措施': '帮助方法',
    '干预': '帮助',
    '筛查': '检查',
    '认知能力下降': '记忆力或思维能力的变化',
    '痴呆症': '认知症',
    '非传染性疾病': '慢性疾病',
    '教育水平较低': '学习机会相对较少',
    '社会孤立': '缺少与他人的交往',
    '体育锻炼': '适度运动',
    '认知刺激': '动脑活动',
    '社会参与': '参与社区活动'
  },
  
  tonePatterns: [
    { from: /应当([^。，]+)/, to: '我们建议您$1' },
    { from: /必须([^。，]+)/, to: '重要的是$1' },
    { from: /需要([^。，]+)/, to: '可以考虑$1' },
    { from: /建议([^。，]+)/, to: '您可以$1' },
    { from: /进行([^。，]+)训练/, to: '学习$1方法' },
    { from: /实施([^。，]+)项目/, to: '参加$1活动' },
    { from: /提供([^。，]+)建议/, to: '为您提供$1指导' },
    { from: /可能包括/, to: '比如说会有' },
    { from: /表现不同/, to: '每个人的情况都不一样' },
    { from: /影响因素/, to: '可能的原因' },
    { from: /应对方法/, to: '我们可以做什么' }
  ]
};

/**
 * 领域映射配置
 */
const DOMAIN_MAPPING: { [key: string]: { 
  chapterPrefix: string;
  keywords: string[];
  displayName: string;
}} = {
  cognition: { chapterPrefix: 'C5', keywords: ['认知', '认知能力'], displayName: '认知能力' },
  mobility: { chapterPrefix: 'C6', keywords: ['行动', '行动能力'], displayName: '行动能力' },
  vitality: { chapterPrefix: 'C7', keywords: ['营养', '营养不足'], displayName: '营养状况' },
  vision: { chapterPrefix: 'C8', keywords: ['视力'], displayName: '视力健康' },
  hearing: { chapterPrefix: 'C9', keywords: ['听力'], displayName: '听力健康' },
  psychologicalCapacity: { chapterPrefix: 'C10', keywords: ['抑郁', '心理'], displayName: '心理健康' },
  socialCareSupport: { chapterPrefix: 'C11', keywords: ['社会', '社会支持'], displayName: '社会照护支持' },
  carerSupport: { chapterPrefix: 'C12', keywords: ['照护者'], displayName: '照护者支持' },
  urinaryIncontinence: { chapterPrefix: 'C13', keywords: ['尿失禁', '膀胱'], displayName: '尿失禁' }
};

/**
 * 内容转化引擎
 */
class ContentTransformationEngine {
  transformForAudience(content: string, targetAudience: 'older_person' | 'family_caregiver' = 'older_person'): string {
    if (!content) return '';
    let transformed = content;
    
    // 替换专业术语
    Object.entries(AUDIENCE_TRANSFORMATION_RULES.terminology).forEach(([original, friendly]) => {
      transformed = transformed.replace(new RegExp(original, 'g'), friendly);
    });
    
    // 调整语气
    AUDIENCE_TRANSFORMATION_RULES.tonePatterns.forEach(({ from, to }) => {
      transformed = transformed.replace(from, to);
    });
    
    // 针对家属微调
    if (targetAudience === 'family_caregiver') {
      transformed = transformed.replace(/您/g, '您的家人')
        .replace(/我们建议您的家人/g, '我们建议您帮助家人');
    }
    
    return transformed.trim();
  }
  
  generateEmpathyIntro(domainName: string): string {
    const templates: { [key: string]: string } = {
      '认知能力': '我们理解记忆力变化可能带来的担忧。',
      '行动能力': '行动不如以前方便是很多人都会遇到的情况。',
      '营养状况': '保持良好的营养对健康非常重要。',
      '视力健康': '视力变化是需要我们共同关注的问题。',
      '听力健康': '听力健康直接影响我们的日常交流。',
      '心理健康': '情绪起伏是人生的正常体验。',
      '社会照护支持': '获得适当的社会支持对健康生活很重要。',
      '照护者支持': '照护他人是一项充满爱心但也有挑战的工作。',
      '尿失禁': '尿失禁问题比想象中更常见。'
    };
    return templates[domainName] || '我们关注您的健康状况。';
  }
}

/**
 * 基于知识库的内容生成器
 */
export class KnowledgeBasedContentGenerator {
  private transformEngine: ContentTransformationEngine;
  private hubUID = 'ICOPE-C3-S3.1.5-Community-Care-Overview';
  
  constructor() {
    this.transformEngine = new ContentTransformationEngine();
  }
  
  private extractDomainInterventionFromHub(domainKey: string): string {
    const hubBlock = knowledgeBaseService.getKnowledgeBlock(this.hubUID);
    if (!hubBlock?.content) {
      return `关于${DOMAIN_MAPPING[domainKey]?.displayName || domainKey}的社区支持`;
    }
    
    const domainConfig = DOMAIN_MAPPING[domainKey];
    if (!domainConfig) return '';
    
    // 从Hub内容中提取领域特定的干预描述
    for (const keyword of domainConfig.keywords) {
      const regex = new RegExp(`-\\s*\\*\\*${keyword}[^*]*\\*\\*[：:](.*?)(?=\\s*-|$)`, 's');
      const match = hubBlock.content.match(regex);
      if (match?.[1]) {
        return match[1].trim().replace(/\s+/g, ' ');
      }
    }
    
    return `社区可以为${domainConfig.displayName}方面的问题提供专业的支持和指导。`;
  }
  
  private extractActionStepsFromRelated(domainKey: string): string[] {
    const actionSteps: string[] = [];
    
    // 基于原始知识块内容为每个领域生成具体的老年友好行动步骤
    switch (domainKey) {
      case 'cognition':
        actionSteps.push('每周3-4次适度运动');
        actionSteps.push('参加太极或有氧运动');  
        actionSteps.push('加入兴趣小组或棋类活动');
        actionSteps.push('学习日常照护技巧');
        actionSteps.push('创建安全家居环境');
        break;
        
      case 'mobility':
        actionSteps.push('加入快走小组');
        actionSteps.push('参加公园太极班');
        actionSteps.push('进行肌肉力量训练');
        actionSteps.push('改善家居安全环境');
        actionSteps.push('使用必要的辅助工具');
        break;
        
      case 'vitality':
        actionSteps.push('增加蛋白质食物摄入');
        actionSteps.push('每天吃豆类坚果鱼类');
        actionSteps.push('结合适度运动锻炼');
        actionSteps.push('制定个性化饮食计划');
        actionSteps.push('定期监测体重变化');
        break;
        
      case 'vision':
        actionSteps.push('试戴合适度数的老花镜');
        actionSteps.push('改善家中照明环境');
        actionSteps.push('定期检查眼睛健康');
        actionSteps.push('控制血压保护视力');
        actionSteps.push('学习正确用眼方法');
        break;
        
      case 'hearing':
        actionSteps.push('学习简单交流技巧');
        actionSteps.push('在安静环境中对话');
        actionSteps.push('考虑使用助听设备');
        actionSteps.push('告知家人听力情况');
        actionSteps.push('减少背景噪音干扰');
        break;
        
      case 'psychologicalCapacity':
        actionSteps.push('学习深呼吸放松法');
        actionSteps.push('保持与家友联系');
        actionSteps.push('参加喜欢的活动');
        actionSteps.push('寻求专业情感支持');
        actionSteps.push('建立规律作息习惯');
        break;
        
      case 'socialCareSupport':
        actionSteps.push('了解可用社区服务');
        actionSteps.push('参与社区活动');
        actionSteps.push('建立社会支持网络');
        actionSteps.push('与邻居保持友好');
        actionSteps.push('主动寻求帮助');
        break;
        
      case 'carerSupport':
        actionSteps.push('学习照护技能方法');
        actionSteps.push('加入照护者互助组');
        actionSteps.push('安排自己的休息时间');
        actionSteps.push('寻求情感支持帮助');
        actionSteps.push('了解照护资源信息');
        break;
        
      case 'urinaryIncontinence':
        actionSteps.push('学习膀胱训练方法');
        actionSteps.push('调整饮水时间安排');
        actionSteps.push('进行盆底肌肉锻炼');
        actionSteps.push('使用合适辅助用品');
        actionSteps.push('保持良好卫生习惯');
        break;
        
      default:
        actionSteps.push('咨询社区专业人员');
        actionSteps.push('参加健康教育活动');
        actionSteps.push('制定个人改善计划');
    }
    
    // 为每个领域返回至少3条行动步骤
    const selectedSteps = actionSteps.slice(0, Math.max(3, actionSteps.length));
    return selectedSteps.length > 0 ? selectedSteps : ['咨询社区专业人员获取指导'];
  }
  
  private getDeepDiveContentFromChapterOverview(domainKey: string): string {
    const domainConfig = DOMAIN_MAPPING[domainKey];
    if (!domainConfig) return '请咨询专业人员了解更多信息。';
    
    const overviewUID = `ICOPE-${domainConfig.chapterPrefix}-Overview`;
    const overviewBlock = knowledgeBaseService.getKnowledgeBlock(overviewUID);
    
    if (!overviewBlock) {
      return '请咨询专业人员了解更多关于此领域的专业背景信息。';
    }
    
    // 构建丰富的深度指导内容
    return this.buildRichDeepDiveContent(domainKey, overviewBlock);
  }
  
  private buildRichDeepDiveContent(domainKey: string, overviewBlock: any): string {
    const domainConfig = DOMAIN_MAPPING[domainKey];
    let deepContent = '';
    
    if (domainKey === 'cognition') {
      // 基于ICOPE-C5-Overview的verbatim_content构建老年友好的认知能力深度指导
      deepContent = `
**了解记忆和思维的变化**

每个人的记忆和思维变化都不一样，比如说会有：容易忘事、注意力不够集中、说话时找词困难、判断事情时不如以前、解决问题比较吃力等。

请放心，这些变化并不意味着您一定会得认知症。实际上，记忆和思维的变化并不是年龄增长的必然结果，很多老年朋友都不会遇到这些问题。

**可能的原因**

记忆和思维的变化可能与以下情况有关：
- 听力不太好、心情低落、血压高、血糖高等健康问题
- 平时运动较少、与人交往不多、学习机会相对较少

**我们可以做什么**

好消息是，我们有很多方法可以帮助保持大脑健康：
- 社区专业人员可以为您做初步的检查评估
- 通过健康的生活方式来保护大脑：适度运动、动脑活动、多与人交流
- 每个人的情况不同，需要找到最适合自己的方法

**获得支持，维护尊严**

如果您或家人有记忆和思维方面的变化，请记住：
- 您应该得到尊重和理解，而不是被误解或歧视
- 保持与家人朋友的联系非常重要
- 照护家人的朋友们也需要支持和指导，这样才能更好地帮助您
- 社区里有专业人员可以为您和家人提供帮助`;
      
    } else if (domainKey === 'mobility') {
      deepContent = `
**了解行动能力的重要性**

能够自由地走动对于保持独立生活非常重要。如果您发现走路、上下楼梯或日常活动变得比以前困难，这是很常见的情况，不用过分担心。

**我们可以做什么**

通过适当的运动和安全措施，您可以改善和保持行动能力：
- 参加适合自己的安全运动项目
- 加强腿部肌肉力量和身体平衡
- 在家里和社区创造更安全的环境
- 必要时使用辅助工具，这并不丢脸，而是明智的选择

社区里有专业人员可以指导您选择最适合的运动方式，确保既有效又安全。`;
      
    } else if (domainKey === 'vitality') {
      deepContent = `
**了解营养对健康的影响**

良好的营养对于保持体力和精神状态都很重要。如果您感到体力不如以前，或者体重有明显变化，合理的饮食调整可能会有帮助。

**我们可以做什么**

保持良好营养状况的方法：
- 多吃富含蛋白质的食物，如鸡蛋、鱼类、豆类等
- 保证每天有足够的蔬菜和水果
- 适量饮水，但不要过量
- 如果胃口不好或吞咽困难，可以请教专业人员

记住，每个人的营养需求都不同，最好咨询营养师或社区专业人员，为您制定最合适的饮食计划。`;
    } else if (domainKey === 'vision') {
      deepContent = `
**了解视力变化**

随着年龄增长，视力出现一些变化是正常的，比如看近处的东西不如以前清楚，或者在暗处看东西困难。但这不意味着我们就要接受视力不断下降。

**我们可以做什么**

保护和改善视力的方法：
- 定期检查眼睛，及时配戴合适的眼镜
- 注意控制血压，因为高血压会影响眼睛健康
- 保持家里光线充足，尤其是阅读和做事时
- 如果有眼睛不适，及时看医生

社区可能有免费或低价的视力检查服务，不要因为费用问题而耽误了眼睛保健。`;
      
    } else if (domainKey === 'hearing') {
      deepContent = `
**了解听力变化**

听力变化会影响我们与家人朋友的交流，也可能让我们感到孤独。如果您发现听电话、看电视或与人聊天时有困难，这是可以改善的。

**实用的交流技巧**

改善交流效果的方法：
- 与人交谈时，让对方能看到您的脸，保持眼神交流
- 选择光线充足、安静的环境聊天，减少背景噪音
- 可以请对方说话慢一些、清楚一些，但不用大声喊叫
- 在群体聊天时，建议大家轮流说话，避免同时说话

**寻求帮助与支持**

- 告诉家人朋友您的听力情况，请他们多一些耐心
- 必要时可以考虑使用助听设备，这并不丢脸
- 可以使用手势、写字等方式辅助沟通
- 不要因为听力问题而避免与人交往，社区有专业人员可以教您更多方法`;
      
    } else if (domainKey === 'psychologicalCapacity') {
      deepContent = `
**了解情绪的变化**

感到心情低落、焦虑或压力大是很常见的，尤其是面对健康变化或生活变化时。这些感受是正常的，也是可以改善的。

**我们可以做什么**

照顾好自己情绪健康的方法：
- 学习一些简单的放松技巧，比如深呼吸、听音乐
- 保持与家人朋友的联系，不要把烦恼都藏在心里
- 参加自己喜欢的活动，哪怕是很简单的事情
- 如果情绪持续低落，不要不好意思寻求专业帮助

记住，照顾好自己的心情和照顾好身体一样重要，这不是软弱的表现。`;
      
    } else if (domainKey === 'socialCareSupport') {
      deepContent = `
**了解社会支持的重要性**

与家人朋友和社区保持联系对身心健康都很重要。如果您感到孤独或需要帮助时不知道找谁，这些都是可以改善的。

**建立支持网络**

获得社会支持的方法：
- 了解您所在社区提供的各种服务和活动
- 主动参与适合自己的社区活动，如老年大学、兴趣小组等
- 与邻居保持友好关系，互相关心和帮助
- 不要不好意思向家人朋友表达需要帮助

**保持社会联系**

- 定期与家人朋友联系，哪怕是简单的问候
- 参加宗教活动、志愿服务等有意义的社会活动
- 利用现代通讯工具，如电话、视频通话等保持联系
- 记住，寻求帮助是正常的，社区专业人员随时为您提供支持`;
      
    } else if (domainKey === 'carerSupport') {
      deepContent = `
**了解照护者的重要性**

照护家人是一项充满爱心的工作，但也可能带来挑战和压力。照护者的健康和福祉同样重要，需要得到支持和帮助。

**照护技能与支持**

为照护者提供的帮助：
- 学习基本的照护技能和方法，让照护工作更有效
- 了解被照护者的病情和需求，提供更好的照护
- 加入照护者互助小组，分享经验和获得情感支持
- 学会寻求专业帮助，不要什么都自己扛

**照护者自我关怀**

- 安排自己的休息时间，避免过度疲劳
- 保持自己的兴趣爱好和社交活动
- 注意自己的身心健康，定期体检和情感疏导
- 了解可用的照护资源和支持服务，适当寻求帮助`;
      
    } else if (domainKey === 'urinaryIncontinence') {
      deepContent = `
**了解尿失禁问题**

尿失禁问题比想象中更常见，很多人都会遇到。这不是什么羞耻的事情，而是可以通过正确的方法来改善的健康问题。

**改善方法**

尿失禁的改善方法：
- 学习膀胱训练的基本方法，逐步提高控制能力
- 合理安排饮水时间，避免睡前大量饮水
- 进行简单的盆底肌肉锻炼，增强控制力
- 保持良好的个人卫生习惯

**寻求帮助与支持**

- 不要因为害羞而避免寻求医疗帮助
- 了解和使用合适的辅助用品，提高生活质量
- 与医护人员开诚布公地讨论您的情况
- 记住这是医学问题，有专业的解决方案`;
      
    } else {
      // 使用现有内容作为后备
      const content = overviewBlock.content || overviewBlock.verbatim_content;
      if (content) {
        const sentences = content.split(/[。；]/);
        let summary = '';
        for (const sentence of sentences) {
          if ((summary + sentence).length <= 200 && sentence.trim()) {
            summary += sentence.trim() + '。';
          } else break;
        }
        deepContent = summary || content.substring(0, 200) + '...';
      }
    }
    
    return this.transformEngine.transformForAudience(deepContent.trim());
  }
  
  generateAdviceContent(domainKey: string, userProfile?: any) {
    const domainConfig = DOMAIN_MAPPING[domainKey];
    if (!domainConfig) {
      throw new Error(`不支持的领域: ${domainKey}`);
    }
    
    // 1. 整合式社区干预内容生成
    const communityIntervention = this.generateRichCommunityIntervention(domainKey, userProfile);
    
    // 2. 基于具体知识块的行动步骤
    const actionSteps = this.extractActionStepsFromRelated(domainKey);
    
    // 3. 深度指导
    const deepDiveContent = this.getDeepDiveContentFromChapterOverview(domainKey);
    const relatedChapters = [`ICOPE-${domainConfig.chapterPrefix}-Overview`];
    
    // 4. 专业转介
    const professionalReferral = `您的"${domainConfig.displayName}"评估结果提示需要进一步关注。我们建议您就此问题寻求医疗机构的专业评估，以获得更个性化的指导和支持。`;
    
    return {
      communityIntervention: { content: communityIntervention },
      actionSteps,
      deepDive: { content: deepDiveContent, knowledgeBlocks: relatedChapters },
      professionalReferral: { content: professionalReferral, urgency: '建议优先处理' }
    };
  }
  
  private generateRichCommunityIntervention(domainKey: string, userProfile?: any): string {
    const domainConfig = DOMAIN_MAPPING[domainKey];
    const hubBlock = knowledgeBaseService.getKnowledgeBlock(this.hubUID);
    
    // 获取共鸣式开场
    const empathyIntro = this.transformEngine.generateEmpathyIntro(domainConfig.displayName);
    
    // 从Hub获取基础干预描述
    const hubIntervention = this.extractDomainInterventionFromHub(domainKey);
    
    // 从相关知识块获取具体干预详情
    const detailedInterventions = this.extractDetailedInterventions(domainKey);
    
    // 整合生成完整的社区干预内容
    let fullIntervention = `${empathyIntro} `;
    
    if (hubIntervention) {
      const transformedHub = this.transformEngine.transformForAudience(
        hubIntervention, 
        userProfile?.assessmentFor === 'family_member' ? 'family_caregiver' : 'older_person'
      );
      fullIntervention += `${transformedHub} `;
    }
    
    if (detailedInterventions.length > 0) {
      fullIntervention += `具体而言，社区可以为您提供：${detailedInterventions.join('；')}。`;
    }
    
    return fullIntervention.trim();
  }
  
  private extractDetailedInterventions(domainKey: string): string[] {
    const hubBlock = knowledgeBaseService.getKnowledgeBlock(this.hubUID);
    if (!hubBlock?.related) return [];
    
    const domainConfig = DOMAIN_MAPPING[domainKey];
    if (!domainConfig) return [];
    
    const interventions: string[] = [];
    
    // 基于原始知识块内容为每个领域定制详细干预措施
    switch (domainKey) {
      case 'cognition':
        interventions.push('适度运动，包括中等强度的有氧运动以及太极等平衡和肌肉力量锻炼');
        interventions.push('动脑活动，如参加排舞小组、下棋或学习语言等兴趣班');
        interventions.push('为家人提供日常照护的实用建议，包括设置提醒、创建安全环境等');
        break;
        
      case 'mobility':
        interventions.push('组织适合的锻炼活动，如快走小组、公园太极班或社区瑜伽课');
        interventions.push('提供社交机会和同伴支持，鼓励坚持锻炼');
        interventions.push('与相关机构合作，为您争取专用的锻炼时间和安全的锻炼环境');
        break;
        
      case 'vitality':
        interventions.push('高蛋白饮食指导，推荐富含蛋白质的食物如豆类、坚果、鱼类等');
        interventions.push('结合适度运动的建议，帮助身体更好地吸收营养');
        interventions.push('制定适合当地食材和口味习惯的个性化营养计划');
        break;
        
      case 'vision':
        interventions.push('帮助获取合适的老花镜，有多种度数可选择');
        interventions.push('提供使用老花镜的方法指导和购买信息');
        interventions.push('组织流动服务，让您可以试戴不同度数的老花镜');
        break;
        
      case 'hearing':
        interventions.push('教授简单实用的交流技巧和方法');
        interventions.push('提供改善家居听力环境的建议');
        interventions.push('介绍助听设备的使用和获取途径');
        break;
        
      case 'psychologicalCapacity':
        interventions.push('教授压力管理技巧，包括放松和呼吸方法');
        interventions.push('提供情绪支持和倾听服务');
        interventions.push('组织适合的社交活动，帮助保持心理健康');
        break;
        
      case 'socialCareSupport':
        interventions.push('介绍可用的社区服务和活动信息');
        interventions.push('帮助建立社会联系和支持网络');
        interventions.push('提供参与社区活动的机会和指导');
        break;
        
      case 'carerSupport':
        interventions.push('为照护者提供照护技能培训和建议');
        interventions.push('组织照护者互助小组，分享经验和提供情感支持');
        interventions.push('提供照护者休息和支持服务的信息');
        break;
        
      case 'urinaryIncontinence':
        interventions.push('教授膀胱训练的基本方法和技巧');
        interventions.push('提供生活方式调整的建议');
        interventions.push('介绍相关的辅助用品和使用方法');
        break;
        
      default:
        interventions.push('社区专业人员可以为您提供个性化的支持和指导');
        interventions.push('组织相关的健康教育活动和服务');
        interventions.push('协助您获得必要的医疗资源和支持');
    }
    
    return interventions.slice(0, 3);
  }
}

// 导出单例实例
export const knowledgeBasedContentGenerator = new KnowledgeBasedContentGenerator();